import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { SubmissionListItem } from '@jfh/intake-shared';
import { api, type SubmissionGroup } from '../lib/api.js';
import { Icon } from '../components/Icon.js';
import { PageHeader } from '../components/Primitives.js';
import { SubmissionList } from '../inbox/SubmissionList.js';
import { SubmissionDetail } from '../inbox/SubmissionDetail.js';
import { summaryLine, viewCounts, viewGroups, type View } from '../inbox/views.js';
import { useMe } from '../lib/useMe.js';

const GROUP_META: Record<SubmissionGroup, { title: string; subtitle: string }> = {
  customers: { title: 'Customers', subtitle: 'Trade and credit account applications' },
  suppliers: { title: 'Suppliers', subtitle: 'Supplier account applications' },
  leads: { title: 'Leads', subtitle: 'Website enquiries. They go straight to iQ, with no approval.' },
};

const VIEWS: Record<'apps' | 'leads', { key: View; label: string }[]> = {
  apps: [
    { key: 'todo', label: 'To do' },
    { key: 'done', label: 'Done' },
    { key: 'all', label: 'All' },
  ],
  leads: [
    { key: 'all', label: 'All' },
    { key: 'not_in_iq', label: 'Not in iQ' },
  ],
};

const EMPTY: Record<View, { title: string; body: string }> = {
  todo: { title: 'Nothing to do', body: 'Every application has been decided and is in iQ.' },
  done: { title: 'Nothing decided yet', body: 'Decided applications show here.' },
  all: { title: 'No applications yet', body: 'They show here as soon as someone applies.' },
  not_in_iq: { title: 'Everything is in iQ', body: 'Every enquiry reached iQ.' },
};

export function Inbox({ group }: { group: SubmissionGroup }) {
  const meta = GROUP_META[group];
  const isLeads = group === 'leads';
  const me = useMe();
  const views = VIEWS[isLeads ? 'leads' : 'apps'];
  const [items, setItems] = useState<SubmissionListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>(views[0]!.key);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const reload = useCallback(() => {
    api
      .listSubmissions(group)
      .then((r) => {
        setItems(r.submissions);
        setError(null);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Couldn’t load the applications'));
  }, [group]);

  useEffect(() => {
    setItems(null);
    // Honour a deep link (?open=SUB-ID) from a Teams notification card.
    const openId = searchParams.get('open');
    setSelectedId(openId);
    if (openId) {
      searchParams.delete('open');
      setSearchParams(searchParams, { replace: true });
      // Show it whatever state it's in.
      setView('all');
    } else {
      setView(VIEWS[group === 'leads' ? 'leads' : 'apps'][0]!.key);
    }
    setQuery('');
    reload();
    // searchParams intentionally read once per group load (not a dep).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  const counts = useMemo(() => viewCounts(items ?? [], me), [items, me]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const found = (items ?? []).filter(
      (s) => !q || `${s.applicant} ${s.email} ${s.id} sub-${s.id}`.toLowerCase().includes(q),
    );
    return viewGroups(found, view, me);
  }, [items, view, query, me]);
  const shown = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  // Keep a valid selection in the split layout.
  useEffect(() => {
    if (!items) return;
    if (selectedId && shown.some((s) => s.id === selectedId)) return;
    if (selectedId && items.some((s) => s.id === selectedId)) return; // opened by link, outside this view
    setSelectedId(shown[0]?.id ?? null);
  }, [shown, items, selectedId]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid var(--border)', flex: 'none' }}>
        <PageHeader
          title={meta.title}
          subtitle={
            items
              ? isLeads
                ? `${items.length} ${items.length === 1 ? 'enquiry' : 'enquiries'}${counts.not_in_iq ? ` · ${counts.not_in_iq} not in iQ` : ''}`
                : summaryLine(items, me)
              : meta.subtitle
          }
        />
        <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div
            role="tablist"
            aria-label="Which applications"
            style={{ display: 'flex', gap: 2, background: 'var(--bg-sunken)', padding: 3, borderRadius: 'var(--r-md)' }}
          >
            {views.map((v) => (
              <button
                key={v.key}
                role="tab"
                aria-selected={view === v.key}
                onClick={() => setView(v.key)}
                style={{
                  border: 'none',
                  background: view === v.key ? 'var(--bg-surface)' : 'transparent',
                  color: view === v.key ? 'var(--text-1)' : 'var(--text-2)',
                  fontWeight: 600,
                  fontSize: 12.5,
                  padding: '5px 12px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  boxShadow: view === v.key ? 'var(--sh-xs)' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {v.label}
                <span className="tnum" style={{ fontSize: 11, color: 'var(--text-3)' }}>
                  {counts[v.key]}
                </span>
              </button>
            ))}
          </div>
          <div style={{ position: 'relative', flex: 1, minWidth: 180, maxWidth: 320 }}>
            <Icon name="search" size={15} style={{ position: 'absolute', left: 11, top: 11, color: 'var(--text-3)' }} />
            <input
              className="input"
              style={{ paddingLeft: 33 }}
              placeholder="Search by name, email or SUB- reference…"
              aria-label="Search applications"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {error ? (
        <div style={{ padding: 24 }}>
          <div className="card" style={{ padding: 16, color: 'var(--st-declined)' }}>
            {error}
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '380px 1fr', minHeight: 0 }}>
          <div style={{ borderRight: '1px solid var(--border)', overflowY: 'auto' }}>
            {items && (
              <SubmissionList
                groups={groups}
                selectedId={selectedId}
                onSelect={setSelectedId}
                empty={query.trim() ? { title: 'No matches', body: 'Nothing matches that search.' } : EMPTY[view]}
              />
            )}
          </div>
          <div style={{ minWidth: 0, overflow: 'hidden' }}>
            {selectedId ? (
              <SubmissionDetail key={selectedId} id={selectedId} onChanged={reload} />
            ) : (
              <div style={{ padding: 24 }}>
                <PlaceholderPane />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PlaceholderPane() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '64px 24px',
        color: 'var(--text-3)',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: 14,
          background: 'var(--bg-sunken)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 14,
        }}
      >
        <Icon name="inbox" size={24} />
      </div>
      <div style={{ fontWeight: 700, color: 'var(--text-2)', fontSize: 15 }}>
        Pick an application
      </div>
      <div style={{ marginTop: 4, fontSize: 13, maxWidth: 320 }}>
        Choose one from the list to see it and decide.
      </div>
    </div>
  );
}
